/**
 * Hook to access the axios instance from PlanDesigner configuration
 *
 * This hook retrieves the configured axios instance from the PlanDesigner context.
 * If no axios instance is provided in the configuration, it returns undefined,
 * allowing the API client functions to fall back to mock implementations.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const axiosInstance = useApiClient();
 *
 *   // Use in API calls
 *   const { data } = useLoadRfp("rfp-123");
 * }
 * ```
 */

import { AxiosInstance } from "axios";
import { useContext } from "react";

import { PlanDesignerContext } from "../PlanDesigner.provider";

/**
 * Hook to get the axios instance from PlanDesigner configuration
 *
 * @param optional - If true, returns undefined instead of throwing when used outside provider
 * @returns The configured axios instance, or undefined if not configured or outside provider (when optional=true)
 */
export const useApiClient = (optional = false): AxiosInstance | undefined => {
  // Check if we're inside the provider before calling the hook
  const context = useContext(PlanDesignerContext);

  if (context === undefined) {
    if (optional) {
      return undefined;
    }
    throw new Error(
      "useApiClient must be used within a PlanDesignerProvider, or pass optional=true to return undefined outside provider",
    );
  }

  return context.configuration.axiosInstance;
};
